import React, { useState, useRef, useLayoutEffect } from 'react';
import { Globe, Users, Hash, Eye, Pencil, Trash2, Check, X, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SocialIcon, type SocialPlatform } from '../icons/SocialIcon';
import { getValidUrl } from '../../utils/url';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../common/Card';
import { InfoTooltip } from '../common/InfoTooltip';
import { CreatePersonaModal } from '../personas/CreatePersonaModal';
import { CreatePostModal } from '../posts/CreatePostModal';
import { PostPreview } from '../calendar/PostPreview';
import { ErrorHandlingMedia } from '../common/ErrorHandlingMedia';
import { normalizeMedia } from '../../utils/mediaUtils';
import { industries } from '../common/IndustrySelect';

import type { Post } from '../../types/calendar';

interface Member {
  id: string;
  email: string;
  role: string;
  status: string;
  addedAt: string;
  updatedAt: string;
  photoURL?: string;
  firstName?: string;
}

interface SocialAccount {
  accountId: string;
  platform: SocialPlatform;
  pageId: string;
  username?: string;
  profileImage?: string;
}

interface Persona {
  personaId: string;
  personaName: string;
  personaPicture: string;
  personaLanguage: string;
}

interface RecentPost {
  postId: string;
  postTitle: string;
  postContent: string;
  postPlatform: string;
  postStatus: string;
  postMediaUrl: string;
  postCreatedAt: string;
  postUpdatedAt: string;
  postScheduledAt: string | null;
}

export interface BrandDetails {
  id: string;
  name: string;
  description: string;
  logo: string;
  website: string;
  industryId: string;
  values: string[];
  keywords: string[];
  languages: string[];
  members: Member[];
  social_accounts: SocialAccount[];
  personas?: Persona[];
  recentPosts?: RecentPost[];
  marketScope?: string;
  distributionType?: string;
  businessModel?: string;
  priorityMarkets?: string[];
  goToMarketStrategy?: {
    marketScope?: string;
    distributionType?: string;
    businessModel?: string;
    priorityMarkets?: string[];
  };
  visualIdentity?: {
    colorPalette?: {
      primary?: string[];
      secondary?: string[];
      accent?: string[];
      neutral?: string[];
    };
    typography?: {
      headings?: {
        h1?: string;
        h2?: string;
        h3?: string;
      };
      body?: {
        primary?: string;
        secondary?: string;
      };
    };
  };
}

interface BrandCardProps {
  brand: BrandDetails;
  onDelete?: (brand: BrandDetails) => void;
  onAcceptInvite?: (brandId: string) => void;
  onDeclineInvite?: (brandId: string) => void;
}

export const BrandCard: React.FC<BrandCardProps> = ({ 
  brand,
  onDelete,
  onAcceptInvite,
  onDeclineInvite
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isCreatePersonaModalOpen, setIsCreatePersonaModalOpen] = useState(false);
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const [hoveredPost, setHoveredPost] = useState<RecentPost | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [previewHeight, setPreviewHeight] = useState<number>(0);
  const previewRef = useRef<HTMLDivElement>(null);

  
  const activeMembers = brand.members.filter(member => member.status === 'active').length;
  const pendingMembers = brand.members.filter(member => member.status === 'pending').length;
  const currentUserMember = brand.members.find(member => member.email === user?.email);
  const isPending = currentUserMember?.status === 'pending';
  const isAdmin = currentUserMember?.role === 'admin';

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-blue-50 text-blue-700';
      case 'reviewer':
        return 'bg-purple-50 text-purple-700';
      case 'writer':
        return 'bg-green-50 text-green-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(brand);
    }
  };

  const handleModify = () => {
    navigate(`/brand-strategy-lab/${brand.id}/edit`);
  };

  const handleAccept = () => {
    if (onAcceptInvite) {
      onAcceptInvite(brand.id);
    }
  };

  const handleDecline = () => {
    if (onDeclineInvite) {
      onDeclineInvite(brand.id);
    }
  };

  const handleCreateWithAI = () => {
    setIsCreatePersonaModalOpen(false);
    navigate('/ai-personas-builder', { 
      state: { 
        selectedBrandId: brand.id,
        createWithAI: true,
        returnToBrands: true 
      } 
    });
  };

  const handleCreateManually = () => {
    setIsCreatePersonaModalOpen(false);
    navigate('/ai-personas-builder', { 
      state: { 
        selectedBrandId: brand.id,
        createManually: true,
        returnToBrands: true 
      } 
    });
  };

  const handleCreatePost = () => {
    setIsCreatePostModalOpen(true);
  };

  const handleCreatePostWithAI = () => {
    setIsCreatePostModalOpen(false);
    navigate('/content-creation', { 
      state: { 
        selectedBrandId: brand.id,
        returnToBrands: true 
      } 
    });
  };

  const handleCreatePostManually = () => {
    setIsCreatePostModalOpen(false);
    navigate('/content-creation', { 
      state: { 
        selectedBrandId: brand.id,
        createManually: true,
        returnToBrands: true 
      } 
    });
  };

  const handlePostClick = (postId: string) => {
    navigate(`/posts/${postId}/edit`);
  };

  // Helper to map RecentPost to PostPreview Post type
  const mapRecentPostToPost = (post: RecentPost): Post => {
    // Convert postMediaUrl string to proper media object like usePosts does
    let media = null;
    if (post.postMediaUrl && post.postMediaUrl.trim() !== '') {
      media = { 
        url: post.postMediaUrl, 
        type: 'image' as const // Default to image, could be improved with URL analysis
      };
    }

    return {
      id: post.postId,
      brandName: brand.name,
      brandLogo: brand.logo,
      content: post.postContent,
      format: 'regular', // fallback, could be improved if info available
      media,
      platforms: [post.postPlatform],
      status: post.postStatus as any,
      scheduledAt: post.postScheduledAt,
      createdAt: post.postCreatedAt,
      updatedAt: post.postUpdatedAt,
      brandId: brand.id,
    };
  };

  useLayoutEffect(() => {
    if (previewRef.current) {
      setPreviewHeight(previewRef.current.getBoundingClientRect().height);
    }
  }, [hoveredPost]);

  const getOptimalPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = Math.min(800, viewportWidth * 0.9);
    const margin = 20;
    let x = mousePosition.x + 10;
    let y = mousePosition.y - 100;
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }
    if (x < margin) {
      x = margin;
    }
    // Utilise la hauteur réelle mesurée
    if (y + previewHeight + margin > viewportHeight) {
      y = viewportHeight - previewHeight - margin;
    }
    if (y < margin) {
      y = margin;
    }
    return { x, y };
  };

  return (
    <Card>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <img 
            src={brand.logo} 
            alt={brand.name}
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
          <div>
            <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">{brand.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Globe className="w-4 h-4 text-gray-400" />
              {getValidUrl(brand.website) && (
                <a 
                  href={getValidUrl(brand.website)?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {getValidUrl(brand.website)?.hostname}
                </a>
              )}
            </div>
            {currentUserMember?.role && (
              <div className="mt-2">
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${getRoleColor(currentUserMember.role)}`}>
                  {currentUserMember.role}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isPending ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={handleAccept}
                className="inline-flex items-center justify-center w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                <Check className="w-4 h-4 mr-2" />
                {t('common.accept')}
              </button>
              <button
                onClick={handleDecline}
                className="inline-flex items-center justify-center w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                <X className="w-4 h-4 mr-2" />
                {t('common.decline')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm text-gray-500 relative group cursor-pointer">
                <Users className="w-4 h-4" />
                <span>
                  {activeMembers}
                  {pendingMembers > 0 && (
                    <span className="text-orange-500">+{pendingMembers}</span>
                  )}
                </span>
                {/* Custom tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                  {activeMembers === 1 
                    ? t('brands.activeMember', '{{count}} membre actif', { count: activeMembers })
                    : t('brands.activeMembers', '{{count}} membres actifs', { count: activeMembers })
                  }
                  {pendingMembers > 0 && (
                    <>
                      <br />
                      {pendingMembers === 1
                        ? t('brands.pendingMember', '{{count}} membre en attente', { count: pendingMembers })
                        : t('brands.pendingMembers', '{{count}} membres en attente', { count: pendingMembers })
                      }
                    </>
                  )}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/brand-strategy-lab/${brand.id}/edit`)}
                  className="p-2 rounded-lg hover:bg-purple-100 transition-colors group relative"
                  title={t('common.view', 'View')}
                >
                  <Eye className="w-4 h-4 text-primary-600 group-hover:text-primary-700" />
                  {/* Custom tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                    {t('common.view', 'Voir')}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
                  </div>
                </button>
                <button
                  onClick={handleModify}
                  className="p-2 rounded-lg hover:bg-purple-100 transition-colors group relative"
                  title={t('common.modify', 'Modify')}
                >
                  <Pencil className="w-4 h-4 text-primary-600 group-hover:text-primary-700" />
                  {/* Custom tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                    {t('common.modify', 'Modifier')}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
                  </div>
                </button>
                {isAdmin && (
                  <button
                    onClick={handleDelete}
                    className="p-2 rounded-lg hover:bg-red-100 transition-colors group relative"
                    title={t('common.delete', 'Delete')}
                  >
                    <Trash2 className="w-4 h-4 text-red-500 group-hover:text-red-700" />
                    {/* Custom tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                      {t('common.delete', 'Supprimer')}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-red-100"></div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-gray-600 text-sm mb-6 line-clamp-2">
        {brand.description}
      </p>

      <div className="space-y-4">
        {/* Brand Kit section - Temporarily hidden */}
        {/* {brand.visualIdentity && (brand.visualIdentity.colorPalette || brand.visualIdentity.typography) && (() => {
          // Brand Kit section temporarily hidden
        })()} */}

        {/* Personas section */}
        <>
          <hr className="my-4 border-t border-gray-200" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900">
                {brand.personas && brand.personas.length > 0 
                  ? t('brands.personasCount', '{{count}} personas', { count: brand.personas.length })
                  : t('personas.title', 'Personas')
                }
              </h4>
              <button
                onClick={() => setIsCreatePersonaModalOpen(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                {t('personas.createPersona', 'Créer un persona')}
              </button>
            </div>
            {brand.personas && brand.personas.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {brand.personas.map((persona) => (
                  <div
                    key={persona.personaId}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg hover:from-purple-100 hover:to-blue-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/ai-personas-builder', { 
                      state: { 
                        selectedBrandId: brand.id,
                        editPersonaId: persona.personaId,
                        returnToBrands: true 
                      } 
                    })}
                    title={`${t('common.edit')} ${persona.personaName}`}
                  >
                    <img 
                      src={persona.personaPicture} 
                      alt={persona.personaName}
                      className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-800">
                        {persona.personaName}
                      </span>
                      <span className="text-xs text-gray-500 uppercase">
                        {persona.personaLanguage}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mb-3 text-xs text-gray-500 italic">{t('personas.noPersona', 'Aucun persona pour cette marque')}</div>
            )}
          </div>
        </>

        {/* Recent Posts section - Always shown */}
        <>
          <hr className="my-4 border-t border-gray-200" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900">
{brand.recentPosts && brand.recentPosts.length > 0 
                  ? t('brands.recentPosts', 'Your {{count}} recent posts', { count: brand.recentPosts.length })
                  : t('brands.posts', 'Posts')
                }
              </h4>
              <button
                onClick={handleCreatePost}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                {t('posts.createPost', 'Créer un post')}
              </button>
            </div>
            {brand.recentPosts && brand.recentPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {brand.recentPosts.slice(0, 3).map((post) => {
                  // Convert recentPost structure to match what normalizeMedia expects
                  // MyContentHub uses post.media, so we need to create a compatible structure
                  const mediaForNormalization = post.postMediaUrl ? {
                    url: post.postMediaUrl,
                    type: 'image' as const
                  } : null;
                  const normalizedMedia = normalizeMedia(mediaForNormalization);
                  
                  return (
                    <div
                      key={post.postId}
                      className="relative group cursor-pointer"
                      title={`${post.postContent.substring(0, 50)}...`}
                      onClick={() => handlePostClick(post.postId)}
                      onMouseEnter={e => {
                        setHoveredPost(post);
                        setMousePosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={e => {
                        setMousePosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseLeave={() => setHoveredPost(null)}
                    >
                      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        {normalizedMedia ? (
                          <ErrorHandlingMedia
                            src={normalizedMedia.url}
                            alt={post.postTitle}
                            type={normalizedMedia.type}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            controls={false}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <span className="text-xs">📝</span>
                          </div>
                        )}
                        
                        {/* Platform icon overlay */}
                        <div className="absolute top-1 right-1">
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-1">
                            <SocialIcon platform={post.postPlatform as SocialPlatform} size={12} />
                          </div>
                        </div>
                        
                        {/* Media format indicator */}
                        {normalizedMedia && (
                          <div className="absolute bottom-1 right-1 bg-white text-gray-800 text-xs px-1 py-0.5 rounded-full flex items-center shadow-sm border border-gray-200">
                            <span className="text-[10px]">{normalizedMedia.type === 'video' ? '🎬' : '🖼️'}</span>
                          </div>
                        )}
                        
                        {/* Content preview overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-white text-xs font-medium truncate">
                            {post.postContent}
                          </p>
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mb-3 text-xs text-gray-500 italic">{t('posts.noPosts', 'Aucun post pour cette marque')}</div>
            )}
            {hoveredPost && (
              <div
                ref={previewRef}
                className="fixed pointer-events-none z-50"
                style={{ left: getOptimalPosition().x, top: getOptimalPosition().y }}
              >
                <PostPreview post={mapRecentPostToPost(hoveredPost)} />
              </div>
            )}
          </div>
        </>

        {/* Marketing Strategy section */}
        {(() => {
          // Use goToMarketStrategy data if available, fallback to direct fields
          const marketScope = brand.goToMarketStrategy?.marketScope || brand.marketScope;
          const distributionType = brand.goToMarketStrategy?.distributionType || brand.distributionType;
          const businessModel = brand.goToMarketStrategy?.businessModel || brand.businessModel;
          const priorityMarkets = brand.goToMarketStrategy?.priorityMarkets || brand.priorityMarkets;
          
          const marketingTags = [];
          
          // Add industry tag
          if (brand.industryId) {
            const industry = industries.find(ind => ind.id === brand.industryId);
            if (industry) {
              marketingTags.push({
                emoji: industry.emoji,
                text: industry.name
              });
            }
          }
          
          if (marketScope) {
            marketingTags.push({
              emoji: marketScope === 'local' ? '🏠' : marketScope === 'national' ? '🇱🇺' : '🌍',
              text: marketScope
            });
          }
          
          if (businessModel) {
            marketingTags.push({
              emoji: businessModel === 'B2B' ? '🏢' : businessModel === 'B2C' ? '👥' : '💼',
              text: businessModel
            });
          }
          
          if (distributionType) {
            marketingTags.push({
              emoji: distributionType === 'physical' ? '🏪' : distributionType === 'digital' ? '💻' : '🔄',
              text: distributionType
            });
          }
          
          if (priorityMarkets && priorityMarkets.length > 0) {
            priorityMarkets.forEach(market => {
              marketingTags.push({
                emoji: '🎯',
                text: market
              });
            });
          }
          
          return marketingTags.length > 0 && (
            <>
              <hr className="my-4 border-t border-gray-200" />
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">{t('brands.marketingStrategy', 'Marketing Strategy')}</h4>
                <div className="flex flex-wrap gap-2">
                  {marketingTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-lg text-sm"
                    >
                      <span className="text-sm">{tag.emoji}</span>
                      <span className="capitalize">{tag.text}</span>
                    </span>
                  ))}
                </div>
              </div>
            </>
          );
        })()}

        {/* Social accounts section */}
        {brand.social_accounts.length > 0 && (
          <>
            <hr className="my-4 border-t border-gray-200" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">{t('brands.connectedAccounts')}</h4>
              <div className="flex flex-wrap gap-2">
                {brand.social_accounts.map((account) => (
                  <div
                    key={account.accountId}
                    className="flex items-center gap-2 px-3 py-2 bg-primary-100 text-primary-700 rounded-lg"
                  >
                    <SocialIcon platform={account.platform} size={20} />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-primary-700">
                        {account.username || account.platform}
                      </span>
                      {account.profileImage && (
                        <img 
                          src={account.profileImage} 
                          alt={`${account.username}'s profile`}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Associated members section - now after social accounts */}
        {brand.members.filter(m => m.role !== 'admin').length > 0 && (
          <>
            <hr className="my-4 border-t border-gray-200" />
            <div className="mt-2">
              <h4 className="text-sm font-medium text-gray-900 mb-2">{t('brands.associatedMembers', 'Associated members')}</h4>
              <div className="flex flex-wrap gap-4">
                {brand.members
                  .filter(m => m.role !== 'admin')
                  .sort((a, b) => {
                    if (a.status === 'active' && b.status !== 'active') return -1;
                    if (a.status !== 'active' && b.status === 'active') return 1;
                    return 0;
                  })
                  .map((member) => (
                    member.status === 'active' ? (
                      <div key={member.id} className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                        {member.photoURL ? (
                          <img src={member.photoURL} alt={member.firstName} className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm">
                            {member.firstName ? member.firstName.charAt(0).toUpperCase() : '?'}
                          </span>
                        )}
                        <span className="text-sm font-medium text-green-700">{member.firstName}</span>
                      </div>
                    ) : member.status === 'pending' ? (
                      <div key={member.id} className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-2">
                        <span className="text-xs text-orange-700">{member.email}</span>
                      </div>
                    ) : null
                  ))}
              </div>
            </div>
          </>
        )}

        {/* Values section */}
        {brand.values.length > 0 && (
          <>
            <hr className="my-4 border-t border-gray-200" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">{t('brands.values')}</h4>
              <div className="flex flex-wrap gap-2">
                {brand.values.map((value, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm"
                  >
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Keywords section */}
        {brand.keywords.length > 0 && (
          <>
            <hr className="my-4 border-t border-gray-200" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">{t('brands.keywords')}</h4>
              <div className="flex flex-wrap gap-2">
                {brand.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm"
                  >
                    <Hash className="w-3 h-3" />
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Create Persona Modal */}
      <CreatePersonaModal
        isOpen={isCreatePersonaModalOpen}
        onClose={() => setIsCreatePersonaModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
      />
      
      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostModalOpen}
        onClose={() => setIsCreatePostModalOpen(false)}
        onCreateWithAI={handleCreatePostWithAI}
        onCreateManually={handleCreatePostManually}
      />
    </Card>
  );
};